// Instancia i18next aislada para el sitio público (hub + spokes).
// Independiente de los otros dos sistemas i18n del repo:
//   - src/lib/translations.ts          → dashboard / app autenticada
//   - src/contexts/ParentTranslationContext.tsx → parent app
// Mantenerlo aislado evita colisiones de namespaces y permite lazy-load del
// idioma activo sin arrastrar el diccionario del dashboard al bundle del
// sitio público.
//
// Stream C añade un namespace por spoke. El hub sigue usando el namespace
// `landing` por defecto; cada spoke llama `useTranslation('<ns>')` y sus
// keys son relativas al root del JSON correspondiente. Ejemplos:
//   - useTranslation()             → t("hero.title")     → landing.hero.title
//   - useTranslation("pricing")    → t("hero.title")     → pricing.hero.title
//   - useTranslation("legal")      → t("cookies.title")  → legal.cookies.title

import i18n, { type i18n as I18nType } from "i18next";
import { initReactI18next } from "react-i18next";

// Hub — el JSON está envuelto en `{ "landing": { ... } }`, accedemos al
// objeto interno para que el namespace coincida con la estructura.
import esLanding from "@/locales/landing/es.json";

// Spokes — JSON sin wrapper, cada archivo es directamente el namespace.
import esPricing from "@/locales/pricing/es.json";
import esForChains from "@/locales/forChains/es.json";
import esContact from "@/locales/contact/es.json";
import esAbout from "@/locales/about/es.json";
// LDT-741 · Del namespace `legal`, la portada solo necesita el banner de
// cookies: va aparte (`cookieBanner`). Los textos legales en castellano
// (~60 KB y creciendo) se cargan con loadLegalTexts() al abrir una página
// legal, no en el trozo de entrada.
import esCookieBanner from "@/locales/cookieBanner/es.json";
import esFeatures from "@/locales/features/es.json";
import { safeLocalStorage } from "./safe-storage";

export type LandingLang = "es" | "ca" | "en";

const STORAGE_KEY = "playium_landing_lang";

function detectInitialLanguage(): LandingLang {
  if (typeof window === "undefined") return "es";
  const stored = safeLocalStorage.getItem(STORAGE_KEY);
  if (stored === "es" || stored === "ca" || stored === "en") return stored;
  // Sin preferencia guardada: respetamos navigator.language pero sólo entre
  // los 3 idiomas soportados; si el navegador habla francés, ruso, etc. se
  // queda en castellano (default del sitio).
  const nav = (window.navigator.language || "es").slice(0, 2).toLowerCase();
  if (nav === "ca" || nav === "en") return nav;
  return "es";
}

const landingI18n: I18nType = i18n.createInstance();

void landingI18n.use(initReactI18next).init({
  resources: {
    es: {
      landing: esLanding.landing,
      pricing: esPricing,
      forChains: esForChains,
      contact: esContact,
      about: esAbout,
      cookieBanner: esCookieBanner,
      features: esFeatures,
    },
  },
  // LDT-718 · Solo el español viene cargado (va en el trozo de la landing y es
  // el idioma de respaldo). Catalán e inglés: loadLandingLanguage().
  lng: detectInitialLanguage(),
  fallbackLng: "es",
  defaultNS: "landing",
  ns: ["landing", "pricing", "forChains", "contact", "about", "legal", "cookieBanner", "features"],
  interpolation: { escapeValue: false },
  returnObjects: true,
  returnEmptyString: false,
});

// LDT-718 · Los tres idiomas viajaban juntos en el trozo de entrada (~65 KB
// comprimidos, sobre todo los textos legales). Catalán e inglés van ahora en
// su propio trozo y se cargan cuando hacen falta:
//   · main.tsx, antes de montar, el de la URL (y el guardado, en la app);
//   · el prerender, antes de pintar cada página;
//   · el selector de idioma y <LangLayout>, al cambiar.
const LOADERS: Record<Exclude<LandingLang, "es">, () => Promise<{ default: Record<string, unknown> }>> = {
  ca: () => import("./i18n-landing-ca"),
  en: () => import("./i18n-landing-en"),
};

const loading = new Map<LandingLang, Promise<void>>();

/** ¿Están ya los textos de este idioma? El español, siempre. */
export function isLandingLanguageLoaded(lang: LandingLang): boolean {
  return lang === "es" || landingI18n.hasResourceBundle(lang, "landing");
}

/** Carga (una vez) los textos de un idioma. */
export function loadLandingLanguage(lang: LandingLang): Promise<void> {
  if (isLandingLanguageLoaded(lang)) return Promise.resolve();
  let p = loading.get(lang);
  if (!p) {
    p = LOADERS[lang as Exclude<LandingLang, "es">]().then(({ default: bundle }) => {
      for (const [ns, res] of Object.entries(bundle)) {
        landingI18n.addResourceBundle(lang, ns, res, true, true);
      }
    });
    loading.set(lang, p);
    // Un fallo de red no deja la promesa envenenada: el siguiente intento reintenta.
    p.catch(() => loading.delete(lang));
  }
  return p;
}

/** Guarda la preferencia y cambia de idioma en cuanto sus textos están
 *  cargados (LDT-718: catalán e inglés llegan en su trozo). La promesa
 *  resuelve con el idioma ya cambiado. */
export function setLandingLanguage(lang: LandingLang): Promise<void> {
  if (typeof window !== "undefined") {
    safeLocalStorage.setItem(STORAGE_KEY, lang);
  }
  // Con los textos ya cargados, el cambio es inmediato, como antes de LDT-718.
  if (isLandingLanguageLoaded(lang)) {
    void landingI18n.changeLanguage(lang);
    return Promise.resolve();
  }
  return loadLandingLanguage(lang).then(() => {
    void landingI18n.changeLanguage(lang);
  });
}

let legalEs: Promise<void> | null = null;

/** LDT-741 · Carga (una vez) los textos legales en castellano. Catalán e
 *  inglés ya los traen sus trozos de idioma (i18n-landing-ca/en.ts). Cada
 *  página legal lo espera en su lazy() de App.tsx, así que están antes de
 *  pintar, al hidratar y en el prerender. */
export function loadLegalTexts(): Promise<void> {
  if (landingI18n.hasResourceBundle("es", "legal")) return Promise.resolve();
  if (!legalEs) {
    legalEs = import("@/locales/legal/es.json").then(({ default: legal }) => {
      landingI18n.addResourceBundle("es", "legal", legal, true, true);
    });
    // Un fallo de red no deja la promesa envenenada.
    legalEs.catch(() => {
      legalEs = null;
    });
  }
  return legalEs;
}

export default landingI18n;
